//path: src\utils\dataConverters.ts

import { OrgInstance, SupabaseOrgInstance, SupabaseOrgMember, OrgMember } from '@/types/orgTypes';
import {
  SupabaseTemplateSubmission,
  TemplateSubmission
} from '@/types/templateSubmissionTypes';
import {
  SupabaseProjectInstance,
  ProjectInstance,
  ProjectCoverImages
} from '../types/projectTypes';
import {
  UIImage,
  SupabaseUIImage
} from '@/modules/uiImages/types';
import { SupabaseSceneInstance, SceneInstance, SupabaseSceneOrigin, SceneOrigin } from '../types/sceneTypes';
import {
  SupabaseAssetType,
  Asset,
  SupabaseAssetInstance,
  AssetInstance
} from '@/types/assetTypes';
import {
  SupabaseClipType,
  Clip,
  SupabaseClipInstance,
  ClipInstance,
  CLIP_CATEGORIES,
  isFadeSource,
  isTweenLikeCategory
} from '@/types/clipTypes';
import { Uid } from './utils';
import { safeParseJson } from './safeParseJson';
import { Story } from '@/types/cmsTypes';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { isBackgroundAsset } from '@/pages/editor/components/timeline/TimelineCategoryItem/assetCapabilities';
import { INITIAL_TIMELINE_LENGTH_SECONDS } from '@/pages/editor/components/timeline/utils';

/**
 * Normalizes location info so address and city are never null - uses "null" string instead.
 * This prevents app breaks when null values are used in string contexts (e.g. display, form inputs).
 */
function normalizeLocationInfo<T extends { address?: string | null; city?: string | null } | null>(
  locationInfo: T
): T {
  if (!locationInfo) return locationInfo;
  const parsed = safeParseJson<T>(locationInfo);
  if (!parsed) return locationInfo;
  return {
    ...parsed,
    address: parsed.address ?? 'null',
    city: parsed.city ?? 'null'
  } as T;
}

export const convertToProjectInstance = (
  supabaseProjectInstance: SupabaseProjectInstance
): ProjectInstance => {
  return {
    id: supabaseProjectInstance.project_instance_id,
    orgId: supabaseProjectInstance.org_instance_id,
    userId: supabaseProjectInstance.user_id,
    projectCategory: supabaseProjectInstance.project_instance_category,
    projectName: supabaseProjectInstance.project_instance_display_name,
    sourceName: supabaseProjectInstance.project_instance_source_name,
    projectCoverImgUrls: supabaseProjectInstance.project_instance_cover_img_urls || [],
    projectCoverImages: supabaseProjectInstance.project_instance_cover_images || null,
    description: supabaseProjectInstance.project_instance_description,
    locationInfo: normalizeLocationInfo(supabaseProjectInstance.project_instance_location_info),
    totalScenes: supabaseProjectInstance.project_instance_total_scenes,
    totalDuration: supabaseProjectInstance.project_instance_total_duration,
    isPublished: supabaseProjectInstance.project_instance_is_published,
    tags: Array.isArray(supabaseProjectInstance.project_instance_tags)
      ? supabaseProjectInstance.project_instance_tags
      : safeParseJson<string[]>(supabaseProjectInstance.project_instance_tags as any) || [],
    updatedAt: supabaseProjectInstance.updated_at,
    createdAt: supabaseProjectInstance.created_at
  };
};

export const convertToSceneInstance = (
  supabaseSceneInstance: SupabaseSceneInstance
): SceneInstance => {
  return {
    sceneId: supabaseSceneInstance.scene_instance_id,
    userId: supabaseSceneInstance.user_id,
    projectId: supabaseSceneInstance.project_instance_id,
    sceneOriginId: supabaseSceneInstance.scene_origin_id,
    sceneName: supabaseSceneInstance.scene_instance_display_name,
    sceneDescription: supabaseSceneInstance.scene_instance_description,
    sceneCoverImgUrl: supabaseSceneInstance.scene_instance_cover_img_url,
    userHasUploadedCoverImage: supabaseSceneInstance.scene_instance_user_has_uploaded_cover_image,
    sceneDurationMs: supabaseSceneInstance.scene_instance_duration_ms,
    isStartingScene: supabaseSceneInstance.scene_instance_is_starting_scene,
    sceneInstanceAnchorCategory: supabaseSceneInstance.scene_instance_anchor_category,
    updatedAt: supabaseSceneInstance.updated_at,
    sceneInstanceLocationInfo: normalizeLocationInfo(supabaseSceneInstance.scene_instance_location_info),
    sceneInstanceStatus: supabaseSceneInstance.scene_instance_status
  };
};

export const convertToSceneOrigin = (
  supabaseSceneOrigin: SupabaseSceneOrigin
): SceneOrigin => {
  return {
    id: supabaseSceneOrigin.id,
    createdAt: supabaseSceneOrigin.created_at,
    updatedAt: supabaseSceneOrigin.updated_at,
    sceneOriginId: supabaseSceneOrigin.scene_origin_id,
    sceneOriginName: supabaseSceneOrigin.scene_origin_name,
    userId: supabaseSceneOrigin.user_id,
    projectInstanceId: supabaseSceneOrigin.project_instance_id,
    sceneOriginDescription: supabaseSceneOrigin.scene_origin_description,
    sceneOriginOrderNumber: supabaseSceneOrigin.scene_origin_order_number,
    sceneOriginCoverImgUrl: supabaseSceneOrigin.scene_origin_cover_img_url,
    userHasUploadedCoverImage: supabaseSceneOrigin.scene_origin_user_has_uploaded_cover_image,
    sceneOriginLocationInfo: normalizeLocationInfo(supabaseSceneOrigin.scene_origin_location_info),
    sceneOriginCategory: supabaseSceneOrigin.scene_origin_category,
    sceneOriginTags: supabaseSceneOrigin.scene_origin_tags,
    sceneOriginDifficulty: supabaseSceneOrigin.scene_origin_difficulty,
    sceneOriginLearnings: supabaseSceneOrigin.scene_origin_learnings,
    sceneOriginFeatures: supabaseSceneOrigin.scene_origin_features,
    visibility: supabaseSceneOrigin.visibility,
    suppressedAt: supabaseSceneOrigin.suppressed_at,
    suppressedReason: supabaseSceneOrigin.suppressed_reason
  };
};

export const convertToAsset = (supabaseAsset: SupabaseAssetType): Asset => {
  return {
    userId: supabaseAsset.user_id,
    typeId: supabaseAsset.asset_type_id,
    typeSourceName: supabaseAsset.asset_type_source_name,
    typeDisplayName: supabaseAsset.asset_type_display_name,
    typeImgUrl: supabaseAsset.asset_type_img_url,
    type: supabaseAsset.asset_type_category,
    typeDetails: safeParseJson(supabaseAsset.asset_type_details),
    typeTags: supabaseAsset.asset_type_tags,
    typeClips: supabaseAsset.asset_type_clips?.map((clip) => ({
      typeId: clip.clip_type_id,
      typeSourceName: clip.clip_type_source_name,
      typeDisplayName: clip.clip_type_display_name,
      typeDuration: clip.clip_type_duration,
      displayOn: clip.clip_type_display_on,
      category: clip.clip_type_category
    })),
    typeSource: supabaseAsset.asset_type_source_url || '',
    typePosition: supabaseAsset.asset_type_position || '',
    typeRotation: supabaseAsset.asset_type_rotation || '',
    typeScale: supabaseAsset.asset_type_scale || '',
    typeAltitude: supabaseAsset.asset_type_altitude,
    typeLatitude: supabaseAsset.asset_type_latitude,
    typeLongitude: supabaseAsset.asset_type_longitude
  };
};

export const convertToAssetInstance = (
  supabaseAssetInstance: SupabaseAssetInstance
): AssetInstance => {
  return {
    userId: supabaseAssetInstance.user_id,
    sceneInstanceId: supabaseAssetInstance.scene_instance_id,
    typeId: supabaseAssetInstance.asset_type_id,
    instanceId: supabaseAssetInstance.asset_instance_id,
    instanceSourceName: supabaseAssetInstance.asset_instance_source_name,
    instanceDisplayName: supabaseAssetInstance.asset_instance_display_name,
    instanceImgUrl: supabaseAssetInstance.asset_instance_img_url,
    instanceCategory: supabaseAssetInstance.asset_instance_category,
    instanceDetails: supabaseAssetInstance.asset_instance_details,
    instanceTags: supabaseAssetInstance.asset_instance_tags,
    instanceClips: supabaseAssetInstance.asset_instance_clips?.map(convertToClip),
    instancePosition: supabaseAssetInstance.asset_instance_position,
    instanceRotation: supabaseAssetInstance.asset_instance_rotation,
    instanceScale: supabaseAssetInstance.asset_instance_scale,
    instanceSrcUrl: supabaseAssetInstance.asset_instance_source_url,
    instanceBeginTime: supabaseAssetInstance.asset_instance_begin_time,
    instanceEndTime: supabaseAssetInstance.asset_instance_end_time,
    isLoading: true,
    isVisible: supabaseAssetInstance.asset_instance_is_active ?? true,
    isLocked: (supabaseAssetInstance.asset_instance_category === 'geospatial_anchor' || supabaseAssetInstance.asset_instance_category === 'image_anchor') ? false : (supabaseAssetInstance.asset_instance_is_locked || false),
    isBillboarded: supabaseAssetInstance.asset_instance_is_billboarded ?? false,
    instanceDisplayOrder: supabaseAssetInstance.asset_instance_display_order ?? null,
    instanceAltitude: Number(supabaseAssetInstance.asset_instance_altitude) || 0,
    instanceLongitude: Number(supabaseAssetInstance.asset_instance_longitude) || 0,
    instanceLatitude: Number(supabaseAssetInstance.asset_instance_latitude) || 0
  };
};

/** Normalize scope keys to lowercase so filtering works regardless of DB/backend casing (Character vs character). */
function normalizeClipScope(scope: unknown): Clip['scope'] {
  const obj = safeParseJson<Record<string, unknown>>(scope as string | Record<string, unknown>);
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
  const normalized: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'boolean') normalized[k.toLowerCase()] = v;
  }
  return Object.keys(normalized).length ? normalized : undefined;
}

export const convertToClip = (supabaseClip: SupabaseClipType): Clip => {
  return {
    typeId: supabaseClip.clip_type_id,
    typeSourceName: supabaseClip.clip_type_source_name,
    typeDisplayName: supabaseClip.clip_type_display_name,
    typeDuration: supabaseClip.clip_type_duration,
    displayOn: supabaseClip.clip_type_display_on,
    category: supabaseClip.clip_type_category,
    scope: normalizeClipScope(supabaseClip.clip_type_scope),
    assetTypeId: supabaseClip.asset_type_id,
    sceneInstanceId: supabaseClip.scene_instance_id ?? null
  };
};

export const convertToClipInstance = (
  supabaseClipInstance: SupabaseClipInstance
): ClipInstance => {
  return {
    sceneInstanceId: supabaseClipInstance.scene_instance_id,
    assetId: supabaseClipInstance.asset_instance_id ?? '',
    uiElementId: supabaseClipInstance.ui_element_instance_id ?? null,
    typeId: supabaseClipInstance.clip_type_id,
    instanceId: supabaseClipInstance.clip_instance_id,
    instanceStartTime: supabaseClipInstance.clip_instance_begin_time,
    instanceEndTime: supabaseClipInstance.clip_instance_end_time,
    instanceSourceName: supabaseClipInstance.clip_instance_source_name,
    instanceDisplayName: supabaseClipInstance.clip_instance_display_name,
    instanceDuration: supabaseClipInstance.clip_instance_duration,
    instanceSpeed: supabaseClipInstance.clip_instance_speed || 1.00, // Default to 1.00 if not set
    instanceCategory: supabaseClipInstance.clip_instance_category, // Add category mapping
    clipInstanceSettings: supabaseClipInstance.clip_instance_settings, // Include settings
    triggerAssetInstanceId: supabaseClipInstance.trigger_asset_instance_id ?? null,
    // Only set volume for audio clips, leave undefined for others
    ...(supabaseClipInstance.clip_instance_category === CLIP_CATEGORIES.AUDIO && {
      instanceVolume: supabaseClipInstance.clip_instance_volume ?? 1.00
    })
  };
};

/**
 * Converts a ClipInstance (local state) back to SupabaseClipInstance format
 * Used for sending actions that require the Supabase format without fetching from database
 */
export const convertClipInstanceToSupabase = (
  clipInstance: ClipInstance
): SupabaseClipInstance => {
  return {
    scene_instance_id: clipInstance.sceneInstanceId,
    asset_instance_id: clipInstance.assetId || null,
    ui_element_instance_id: clipInstance.uiElementId ?? null,
    clip_type_id: clipInstance.typeId,
    clip_instance_id: clipInstance.instanceId,
    clip_instance_begin_time: clipInstance.instanceStartTime,
    clip_instance_end_time: clipInstance.instanceEndTime,
    clip_instance_source_name: clipInstance.instanceSourceName,
    clip_instance_display_name: clipInstance.instanceDisplayName,
    clip_instance_duration: clipInstance.instanceDuration,
    clip_instance_category: clipInstance.instanceCategory,
    clip_instance_payload: {}, // Default empty payload
    clip_instance_speed: clipInstance.instanceSpeed,
    trigger_asset_instance_id: clipInstance.triggerAssetInstanceId ?? null,
    // Only include volume for audio clips - always include a number (default to 1.00 if null/undefined)
    // Unity needs a number, not null/undefined, otherwise playback may fail
    ...(clipInstance.instanceCategory === CLIP_CATEGORIES.AUDIO && {
      clip_instance_volume: clipInstance.instanceVolume ?? 1.00
    }),
    ...(clipInstance.clipInstanceSettings && {
      clip_instance_settings: safeParseJson<Record<string, unknown>>(clipInstance.clipInstanceSettings as Record<string, unknown> | string)
    })
  };
};

export const convertAssetInstanceToSupabase = (
  assetInstance: AssetInstance
): SupabaseAssetInstance => {
  return {
    user_id: assetInstance.userId,
    asset_type_id: assetInstance.typeId,
    scene_instance_id: assetInstance.sceneInstanceId,
    asset_instance_id: assetInstance.instanceId,
    asset_instance_source_name: assetInstance.instanceSourceName,
    asset_instance_display_name: assetInstance.instanceDisplayName,
    asset_instance_img_url: assetInstance.instanceImgUrl,
    asset_instance_category: assetInstance.instanceCategory,
    asset_instance_details: assetInstance.instanceDetails,
    asset_instance_is_locked: assetInstance.isLocked,
    asset_instance_is_billboarded: assetInstance.isBillboarded ?? false,
    asset_instance_is_active: assetInstance.isVisible,
    asset_instance_position: assetInstance.instancePosition,
    asset_instance_rotation: assetInstance.instanceRotation,
    asset_instance_scale: assetInstance.instanceScale,
    asset_instance_source_url: assetInstance.instanceSrcUrl,
    asset_instance_begin_time: assetInstance.instanceBeginTime,
    asset_instance_end_time: assetInstance.instanceEndTime,
    asset_instance_altitude: assetInstance.instanceAltitude ?? null,
    asset_instance_longitude: assetInstance.instanceLongitude ?? null,
    asset_instance_latitude: assetInstance.instanceLatitude ?? null,
    asset_instance_display_order: assetInstance.instanceDisplayOrder ?? null,
  };
};

export const convertToOrgInstance = (
  supabaseOrgInstance: SupabaseOrgInstance
): OrgInstance => {
  return {
    id: supabaseOrgInstance.id,
    createdAt: supabaseOrgInstance.created_at,
    orgInstanceId: supabaseOrgInstance.org_instance_id,
    orgInstanceSourceName: supabaseOrgInstance.org_instance_source_name,
    orgTypeId: supabaseOrgInstance.org_type_id,
    orgInstanceDisplayName: supabaseOrgInstance.org_instance_display_name,
    orgInstanceSubscriptionPlan: (supabaseOrgInstance.org_instance_subscription_plan?.trim() || 'starter').toLowerCase(),
    orgInstanceSubscriptionStatus: supabaseOrgInstance.org_instance_subscription_status || 'active',
    orgInstanceBillingCycle: supabaseOrgInstance.org_instance_billing_cycle || 'none',
    orgInstanceNextBillingDate: supabaseOrgInstance.org_instance_next_billing_date,
    orgInstanceImmersalConfigured: supabaseOrgInstance.org_instance_immersal_configured ?? false
  };
};

export const convertToOrgMember = (
  supabaseOrgMember: SupabaseOrgMember
): OrgMember => {
  return {
    id: supabaseOrgMember.id,
    createdAt: supabaseOrgMember.created_at,
    orgInstanceId: supabaseOrgMember.org_instance_id,
    userId: supabaseOrgMember.user_id,
    memberType: supabaseOrgMember.member_type,
    memberPreferences: (() => {
      const prefs = safeParseJson(supabaseOrgMember.member_preferences);
      return {
        hideTutorial: prefs?.hide_tutorial ?? false,
        emailNotifications: prefs?.email_notifications ?? false,
        hideThirdPartyDisclaimer: prefs?.hide_third_party_disclaimer ?? false,
        allowLocationForMapAnchor: prefs?.allow_location_for_map_anchor,
        // Prefer explicit marketing_consent field; fall back to legacy email_notifications for existing records
        marketingConsent: prefs?.marketing_consent
          ?? prefs?.email_notifications
          ?? false,
        marketingConsentDate: prefs?.marketing_consent_date,
      };
    })(),
    memberAllTutorialLessonsComplete: supabaseOrgMember.member_all_tutorial_lessons_complete,
    fullName: supabaseOrgMember.full_name,
    avatarUrl: supabaseOrgMember.avatar_url,
    twoFactorAuthentication: supabaseOrgMember.two_factor_authentication ?? false,
    displayImmersalWatermark: supabaseOrgMember.display_immersal_watermark ?? false,
    trackOneMember: supabaseOrgMember.track_one_member ?? false,
    trackTwoMember: supabaseOrgMember.track_two_member ?? false,
    persona: supabaseOrgMember.persona ?? undefined,
    commercialIntent: supabaseOrgMember.commercial_intent ?? undefined,
    discoverySource: supabaseOrgMember.discovery_source ?? undefined
  };
};

export const convertToTemplateSubmission = (
  row: SupabaseTemplateSubmission
): TemplateSubmission => ({
  id: row.id,
  templateSubmissionId: row.template_submission_id,
  sceneOriginId: row.scene_origin_id,
  sceneInstanceId: row.scene_instance_id ?? null,
  userId: row.user_id,
  status: row.status,
  submittedAt: row.submitted_at ?? null,
  reviewedAt: row.reviewed_at ?? null,
  reviewedBy: row.reviewed_by ?? null,
  rejectionReason: row.rejection_reason ?? null,
  rating: row.rating ?? null,
  shouldBePaid: row.should_be_paid ?? false,
  paidOut: row.paid_out ?? false,
  createdAt: row.created_at,
  updatedAt: row.updated_at
});

export const createAssetInstance = (
  userId: string,
  sceneId: string,
  assetType: SupabaseAssetType,
  additionalData?: {
    duration?: number;
    display_name?: string;
    end_time?: number;
    begin_time?: number;
  }
): SupabaseAssetInstance => {
  console.log('createAssetInstance', additionalData);
  console.log('assetType', assetType);

  // Determine duration based on asset category
  let duration = additionalData?.duration || 60; // default for regular assets
  const assetCategory = assetType.asset_type_category;

  // Set specific durations based on asset category
  if (isBackgroundAsset(assetCategory)) {
    // Background assets are resolved to the live timelineLength at render time
    // via resolveAssetEndTime; the stored value is cosmetic. Seed with the initial
    // canvas length so fresh scenes read sanely.
    duration = INITIAL_TIMELINE_LENGTH_SECONDS;
  } else if (assetCategory === 'character') {
    duration = 10; // 10 seconds for characters
  } else if (assetCategory === 'video' && !additionalData?.duration) {
    // Use actual video file duration from asset type data (set during upload)
    const typeDuration = (assetType.asset_type_end_time || 0) - (assetType.asset_type_begin_time || 0);
    if (typeDuration > 0) {
      duration = typeDuration;
    }
  }

  const beginTime = additionalData?.begin_time || 0;

  const newInstance: Partial<SupabaseAssetInstance> = {
    user_id: userId,
    scene_instance_id: sceneId,
    asset_instance_id: Uid(),
    asset_type_id: assetType.asset_type_id,
    asset_instance_category: (assetType.asset_type_category === 'mask' ? 'prop' : assetType.asset_type_category) || null,
    asset_instance_source_name: assetType.asset_type_source_name,
    asset_instance_display_name: additionalData?.display_name || assetType.asset_type_display_name,
    asset_instance_img_url: assetType.asset_type_img_url,
    asset_instance_details: safeParseJson(assetType.asset_type_details) || null,
    asset_instance_position: assetType.asset_type_position || '',
    asset_instance_rotation: assetType.asset_type_rotation || '',
    asset_instance_scale: assetType.asset_type_scale || '',
    asset_instance_begin_time: beginTime,
    asset_instance_end_time: additionalData?.end_time || (beginTime + duration),
    asset_instance_is_active: assetType.asset_type_is_active,
    asset_instance_source_url: assetType.asset_type_source_url || '',
    asset_instance_is_locked: false,
    asset_instance_is_billboarded: false,
    asset_instance_altitude: assetType.asset_type_altitude,
    asset_instance_latitude: assetType.asset_type_latitude,
    asset_instance_longitude: assetType.asset_type_longitude,
    asset_instance_has_packed_alpha: assetType.asset_type_has_packed_alpha ?? false
  };

  return newInstance as SupabaseAssetInstance;
};

export const createClipInstance = (
  sceneId: string,
  assetId: string,
  clipType: SupabaseClipType,
  beginTime: number,
  duration: number = 0
): SupabaseClipInstance => {
  // Enable looping by default for non-fade, non-tween clips
  const isFade = isFadeSource(clipType.clip_type_source_name);
  const isTween = isTweenLikeCategory(clipType.clip_type_category);
  const shouldEnableLooping = !isFade && !isTween;
  
  // Parse existing settings or create new
  let settings: Record<string, any> = {};
  if (clipType.clip_type_settings) {
    settings = safeParseJson<Record<string, any>>(clipType.clip_type_settings) || {};
  }
  
  // Set looping defaults if enabled
  if (shouldEnableLooping) {
    settings.enableLooping = true;
    settings.numberOfLoops = 1;
  }
  
  return {
    clip_instance_id: Uid(),
    scene_instance_id: sceneId,
    asset_instance_id: assetId,
    clip_type_id: clipType.clip_type_id,
    clip_instance_category: clipType.clip_type_category,
    clip_instance_source_name: clipType.clip_type_source_name,
    clip_instance_display_name: clipType.clip_type_display_name,
    clip_instance_duration: duration,
    clip_instance_begin_time: beginTime,
    clip_instance_end_time: beginTime + duration,
    clip_instance_payload: {},
    clip_instance_settings: Object.keys(settings).length > 0 ? settings : null
  };
};

export const convertSceneInstanceToStory = (
  sceneInstance: SceneInstance,
  order: number = 0
): Story => {
  return {
    id: sceneInstance.sceneId,
    projectId: sceneInstance.projectId,
    title: sceneInstance.sceneName || 'Untitled Scene',
    description: sceneInstance.sceneDescription || `Story ${order} of the project`,
    image: sceneInstance.sceneCoverImgUrl || projectPlaceholder,
    locationInfo: sceneInstance.sceneInstanceLocationInfo,
    order: order,
    status: 'draft', // Default status - will be updated by the component that uses this
    createdAt: sceneInstance.updatedAt ? new Date(sceneInstance.updatedAt) : new Date(),
    updatedAt: sceneInstance.updatedAt ? new Date(sceneInstance.updatedAt) : new Date()
  };
};

export const convertSceneOriginToStory = (
  sceneOrigin: SceneOrigin,
  order: number = 0,
  image?: string,
  imageCrop?: any
): Story => {
  // Extract location info from scene origin if available
  let locationInfo = null;
  if (sceneOrigin.sceneOriginLocationInfo) {
    locationInfo = sceneOrigin.sceneOriginLocationInfo;
  }

  return {
    id: sceneOrigin.sceneOriginId,
    projectId: sceneOrigin.projectInstanceId || '',
    title: sceneOrigin.sceneOriginName || 'Untitled Story',
    description: sceneOrigin.sceneOriginDescription || `Story ${order} of the project`,
    image: image || sceneOrigin.sceneOriginCoverImgUrl || projectPlaceholder,
    imageCrop: imageCrop || undefined,
    locationInfo: locationInfo,
    order: order,
    status: 'draft', // Default status - could be enhanced to check published status
    createdAt: sceneOrigin.createdAt ? new Date(sceneOrigin.createdAt) : new Date(),
    updatedAt: sceneOrigin.updatedAt
      ? new Date(sceneOrigin.updatedAt)
      : sceneOrigin.createdAt ? new Date(sceneOrigin.createdAt) : new Date()
  };
};

// Helper function to convert old string array format to new JSONB format
export const convertLegacyCoverImagesToStructured = (coverImgUrls: string[] | null): ProjectCoverImages => {
  const urls = coverImgUrls || [];

  return {
    portrait: urls[0] ? {
      id: `legacy-1`,
      url: urls[0],
      aspectRatio: '9:16' as const,
      orderNumber: 1
    } : null,
    landscape: urls.slice(1).map((url, index) => ({
      id: `legacy-${index + 2}`,
      url,
      aspectRatio: '16:9' as const,
      orderNumber: index + 1
    }))
  };
};

// Helper function to convert new JSONB format back to legacy string array (for backward compatibility)
export const convertStructuredCoverImagesToLegacy = (coverImages: ProjectCoverImages | null): string[] => {
  if (!coverImages) return [];

  const urls: string[] = [];

  // Add portrait image first
  if (coverImages.portrait) {
    urls.push(coverImages.portrait.url);
  }

  // Add landscape images in order
  const sortedLandscape = [...coverImages.landscape].sort((a, b) => a.orderNumber - b.orderNumber);
  urls.push(...sortedLandscape.map(img => img.url));

  return urls;
};

// Test function to validate the conversion functions
export const testCoverImageConversion = () => {
  const testLegacyUrls = [
    'https://example.com/vertical.jpg',
    'https://example.com/horizontal.jpg',
    'https://example.com/extra1.jpg',
    'https://example.com/extra2.jpg'
  ];

  console.log('Testing conversion functions...');
  console.log('Legacy URLs:', testLegacyUrls);

  const converted = convertLegacyCoverImagesToStructured(testLegacyUrls);
  console.log('Converted to structured:', converted);

  const backToLegacy = convertStructuredCoverImagesToLegacy(converted);
  console.log('Converted back to legacy:', backToLegacy);

  return {
    legacy: testLegacyUrls,
    structured: converted,
    backToLegacy
  };
};

// UI Images conversion functions
export const convertToUIImage = (supabaseUIImage: SupabaseUIImage): UIImage => {
  return {
    id: supabaseUIImage.id,
    createdAt: supabaseUIImage.created_at,
    imageId: supabaseUIImage.image_id,
    projectInstanceId: supabaseUIImage.project_instance_id,
    sceneOriginId: supabaseUIImage.scene_origin_id,
    imageCategory: supabaseUIImage.image_category as any,
    imageMetadata: supabaseUIImage.image_metadata,
    imageOriginalUrl: supabaseUIImage.image_original_url,
    imageCroppedUrl: supabaseUIImage.image_cropped_url,
    userId: supabaseUIImage.user_id
  };
};

export const convertToSupabaseUIImage = (uiImage: Partial<UIImage>): Partial<SupabaseUIImage> => {
  return {
    project_instance_id: uiImage.projectInstanceId,
    scene_origin_id: uiImage.sceneOriginId,
    image_category: uiImage.imageCategory || undefined,
    image_metadata: uiImage.imageMetadata,
    image_original_url: uiImage.imageOriginalUrl,
    image_cropped_url: uiImage.imageCroppedUrl,
    user_id: uiImage.userId
  };
};
